import React, { useState, useEffect, useRef } from "react"
import * as Yup from "yup"
import { Formik, Form, Field } from "formik"
import { toast } from "react-toastify"
import Modal from "../Modal"
import {
  DialogContent,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
} from "@material-ui/core"
import { Copy } from "lucide-react"
import api from "../../services/api"
import toastError from "../../errors/toastError"
import QueueSelect from "../QueueSelect"
import DefaultButton from "../DefaultButton"
import useStyles from "./WhatsAppModalStyles"
import WhatsappModalSkeleton from "./WhatsappModalSkeleton"

const SessionSchema = Yup.object().shape({
  descricao: Yup.string().trim().min(2, "Muito Curto!").max(50, "Muito Longo!").required("Obrigatório"),
  token: Yup.string().required("Não foi possível gerar o token! tente novamente"),
  greetingMessage: Yup.string()
    .trim()
    .transform((value) => (value && value.trim() === "" ? "" : value?.trim()))
    .test(
      "mensagem-saudacao-obrigatoria",
      "Mensagem de saudação é obrigatória quando há mais de uma fila",
      function (value) {
        const { selectedQueueIds } = this.options.context || {}
        if (selectedQueueIds && selectedQueueIds.length > 1) {
          return value && value.trim().length > 0
        }
        return true
      },
    ),
  complationMessage: Yup.string().transform((value) => (value && value.trim() === "" ? "" : value?.trim())),
  outOfHoursMessage: Yup.string().transform((value) => (value && value.trim() === "" ? "" : value?.trim())),
  ratingMessage: Yup.string().transform((value) => (value && value.trim() === "" ? "" : value?.trim())),
})
const tokenGerar = async () => {
  try {
    const response = await api.get("/tokenConnection")
    return response.data.token
  } catch (oErro) {
    toastError("Não foi possível gerar o token: " + oErro.message)
  }
}

const WhatsAppModalFooter = ({ disabled, isSubmitting, onSubmit, classes }) => (
  <div className={classes.buttonsContainer}>
    <DefaultButton
      stylization="primary"
      onClick={onSubmit}
      disabled={disabled}
      className={classes.btnFullWidth}
    >
      Gravar
      {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
    </DefaultButton>
  </div>
)

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles()
  const formikRef = useRef()
  const initialState = {
    descricao: "",
    greetingMessage: `Olá! 👋 Seja bem-vindo ao atendimento digital da [NOME DA EMPRESA]!\nPara que possamos atendê-lo da melhor forma possível, por favor, digite uma das opções abaixo:`,
    complationMessage: `Atendimento finalizado com sucesso! ✅\nAgradecemos pelo seu contato com a [NOME DA EMPRESA]. Caso tenha mais alguma dúvida ou precise de\nnovos serviços, estaremos sempre à disposição para te atender com agilidade e atenção.\nDesejamos um excelente dia! 🚗💨`,
    outOfHoursMessage: `Olá! 👋 Seja bem-vindo ao atendimento digital da [NOME DA EMPRESA]!\nNosso atendimento funciona de segunda a sexta, das 08:00 às 12:00 e das 13:30 às 18:00, e aos sábados das 08:00 às 12:00.\nNo momento estamos fora do expediente, mas você pode deixar sua mensagem por aqui que responderemos\nassim que possível.\nObrigado pelo contato e até breve! 😊`,
    ratingMessage:
      "Obrigado por conversar com a nossa equipe! Para melhorarmos ainda mais, queremos saber como foi seu atendimento. 😊",
    principal: false,
    token: "",
    provider: "beta",
    timeUseBotQueues: 0,
    maxUseBotQueues: 3,
    timeToTransfer: 0,
  }
  const [whatsApp, setWhatsApp] = useState(initialState)
  const [selectedQueueIds, setSelectedQueueIds] = useState([])
  const [selectedQueueId, setSelectedQueueId] = useState(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    const conexaoDadosCarregar = async () => {
      setLoading(true)
      try {
        if (whatsAppId) {
          const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`)
          const whatsAppData = {
            descricao: data.canal?.descricao || "",
            greetingMessage: data.greetingMessage || "",
            complationMessage: data.complationMessage || "",
            outOfHoursMessage: data.outOfHoursMessage || "",
            ratingMessage: data.ratingMessage || "",
            principal: data.canal?.principal || false,
            token: data.token || "",
            provider: data.provider || "beta",
            timeUseBotQueues: data.timeUseBotQueues || 0,
            maxUseBotQueues: data.maxUseBotQueues || 3,
            timeToTransfer: data.timeToTransfer || 0,
            promptId: data.promptId || null,
            integrationId: data.integrationId || null,
          }
          setWhatsApp(whatsAppData)
          const whatsQueueIds = data.queues?.map((queue) => queue.id)
          setSelectedQueueIds(whatsQueueIds || [])
          setSelectedQueueId(data.transferQueueId || null)
        } else {
          const cNovoToken = await tokenGerar()
          setWhatsApp((prev) => ({ ...prev, token: cNovoToken }))
        }
      } catch (oErro) {
        toastError(oErro)
      } finally {
        setLoading(false)
      }
    }

    if (!open) {
      return
    }

    conexaoDadosCarregar()
  }, [open, whatsAppId])

  const validate = (values) => {
    const oErros = {}
    const lTransferenciaTempo = values.timeToTransfer && parseInt(values.timeToTransfer, 10) > 0
    const lTransferenciaFila = !!selectedQueueId

    if (lTransferenciaFila && !lTransferenciaTempo) {
      oErros.timeToTransfer =
        "Para transferência automática, o campo 'Transferir após x (minutos)' também deve ser preenchido."
    }

    if (lTransferenciaTempo && !lTransferenciaFila) {
      oErros.filaTransferencia =
        "Para transferência automática, o campo 'Fila de transferência' também deve ser preenchido."
    }

    return oErros
  }

  const handleSaveWhatsApp = async (values) => {
    const whatsappData = {
      ...values,
      queueIds: selectedQueueIds,
      transferQueueId: selectedQueueId,
      timeToTransfer: values.timeToTransfer === "" ? 0 : values.timeToTransfer,
    }
    delete whatsappData["queues"]
    delete whatsappData["session"]

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData)
      } else {
        await api.post("/whatsapp", whatsappData)
      }
      toast.success("WhatsApp salvo com sucesso.")
      handleClose()
    } catch (err) {
      toastError(err)
    }
  }

  const handleChangeQueue = (e) => {
    const mFilasIds = Array.isArray(e) && e.length > 10 ? e.slice(0, 10) : e

    if (Array.isArray(e) && e.length > 10) {
      toast.error("Uma conexão não pode ter mais de 10 filas associadas")
    }

    setSelectedQueueIds(mFilasIds)

    if (selectedQueueId && Array.isArray(mFilasIds) && !mFilasIds.includes(selectedQueueId)) {
      setSelectedQueueId(null)
      setTimeout(() => {
        formikRef.current?.validateForm()
      }, 0)
      formikRef.current?.setFieldTouched("filaTransferencia", true)
    }
  }

  const handleClose = () => {
    onClose()
    handleClean()
  }
  const handleClean = () => {
    setWhatsApp(initialState)
    setSelectedQueueId(null)
    setSelectedQueueIds([])
  }

  const tokenCopiar = async () => {
    if (!whatsApp.token) {
      toast.error("Token inválido ou inexistente")
      return
    }
    try {
      await navigator.clipboard.writeText(whatsApp.token)
      toast.success("Token copiado com sucesso!")
    } catch (error) {
      toast.error("Falha ao copiar o token", error)
    }
  }

  return (
    <div className={classes.root}>
      {loading ? (
        <Modal
          title={whatsAppId ? "Editar conexão" : "Nova conexão"}
          modalOpen={open}
          onClose={onClose}
          cleanFunc={handleClean}
        >
          <WhatsappModalSkeleton />
        </Modal>
      ) : (
        <Formik
          innerRef={formikRef}
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          validate={validate}
          validateOnChange={true}
          validateOnBlur={true}
          context={{ selectedQueueIds }}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              const cValoresLimpos = {
                ...values,
                greetingMessage: values.greetingMessage?.trim() || "",
                complationMessage: values.complationMessage?.trim() || "",
                outOfHoursMessage: values.outOfHoursMessage?.trim() || "",
                ratingMessage: values.ratingMessage?.trim() || "",
              }
              handleSaveWhatsApp(cValoresLimpos)
              actions.setSubmitting(false)
            }, 400)
          }}
        >
          {({ values, touched, errors, isSubmitting, submitForm, setFieldValue, setFieldTouched, validateForm }) => (
            <Modal
              title={whatsAppId ? "Editar conexão" : "Nova conexão"}
              modalOpen={open}
              onClose={onClose}
              cleanFunc={handleClean}
              footer={
                <WhatsAppModalFooter
                  disabled={
                    isSubmitting ||
                    Boolean(errors.descricao) ||
                    Boolean(errors.timeToTransfer) ||
                    Boolean(errors.filaTransferencia)
                  }
                  isSubmitting={isSubmitting}
                  onSubmit={() => {
                    setFieldTouched("filaTransferencia", true)
                    setFieldTouched("timeToTransfer", true)
                    submitForm()
                  }}
                  classes={classes}
                />
              }
            >
              <Form>
                <DialogContent className={classes.content}>
                  <Grid style={{ width: "100%" }} item>
                    <Field
                      as={TextField}
                      label="Nome"
                      autoFocus
                      name="descricao"
                      error={touched.descricao && Boolean(errors.descricao)}
                      helperText={touched.descricao && errors.descricao}
                      variant="outlined"
                      className={`${classes.textField} ${
                        touched.descricao && errors.descricao ? classes.errorField : classes.background
                      }`}
                      style={{ height: "52px", width: "100%" }}
                    />
                  </Grid>

                  <Grid style={{ width: "100%" }} item>
                    <FormControlLabel
                      control={<Field as={Switch} color="primary" name="principal" checked={values.principal} />}
                      label="Número principal"
                      style={{ marginLeft: "auto" }}
                      className={classes.textField}
                    />
                  </Grid>
                  <div>
                    <Field
                      as={TextField}
                      label="Mensagem de saudação"
                      type="greetingMessage"
                      multiline
                      rows={4}
                      fullWidth
                      name="greetingMessage"
                      error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                      helperText={touched.greetingMessage && errors.greetingMessage}
                      variant="outlined"
                      className={`${classes.textField} ${
                        touched.greetingMessage && errors.greetingMessage ? classes.errorField : classes.background
                      }`}
                    />
                  </div>
                  <div>
                    <Field
                      as={TextField}
                      label="Mensagem de conclusão"
                      type="complationMessage"
                      multiline
                      rows={4}
                      fullWidth
                      name="complationMessage"
                      error={touched.complationMessage && Boolean(errors.complationMessage)}
                      helperText={touched.complationMessage && errors.complationMessage}
                      variant="outlined"
                      className={`${classes.textField} ${
                        touched.complationMessage && errors.complationMessage ? classes.errorField : classes.background
                      }`}
                    />
                  </div>
                  <div>
                    <Field
                      as={TextField}
                      label="Mensagem de fora de expediente"
                      type="outOfHoursMessage"
                      multiline
                      rows={4}
                      fullWidth
                      name="outOfHoursMessage"
                      error={touched.outOfHoursMessage && Boolean(errors.outOfHoursMessage)}
                      helperText={touched.outOfHoursMessage && errors.outOfHoursMessage}
                      variant="outlined"
                      className={`${classes.textField} ${
                        touched.outOfHoursMessage && errors.outOfHoursMessage ? classes.errorField : classes.background
                      }`}
                    />
                  </div>
                  <div>
                    <Field
                      as={TextField}
                      label="Mensagem de avaliação"
                      type="ratingMessage"
                      multiline
                      rows={4}
                      fullWidth
                      name="ratingMessage"
                      error={touched.ratingMessage && Boolean(errors.ratingMessage)}
                      helperText={touched.ratingMessage && errors.ratingMessage}
                      variant="outlined"
                      className={`${classes.textField} ${
                        touched.ratingMessage && errors.ratingMessage ? classes.errorField : classes.background
                      }`}
                    />
                  </div>
                  <div className={classes.tokenRow}>
                    <Field
                      as={TextField}
                      label="Token"
                      type="token"
                      name="token"
                      variant="outlined"
                      disabled
                      className={`${classes.tokenField} ${
                        touched.token && errors.token ? classes.errorField : classes.background
                      }`}
                      value={values.token}
                      error={touched.token && Boolean(errors.token)}
                      helperText={touched.token && errors.token}
                    />
                    <IconButton className={classes.tokenIcons} onClick={tokenCopiar}>
                      <Copy size={"20px"} />
                    </IconButton>
                  </div>
                  <QueueSelect
                    selectedQueueIds={selectedQueueIds}
                    onChange={(selectedIds) => handleChangeQueue(selectedIds)}
                  />
                  <div>
                    <h3>Redirecionamento de fila</h3>
                    <p>Selecione uma fila para os contatos que não possuem fila serem redirecionados</p>
                    <Grid>
                      <Grid item>
                        <Field
                          fullWidth
                          type="number"
                          as={TextField}
                          label="Transferir após x (minutos)"
                          name="timeToTransfer"
                          error={touched.timeToTransfer && Boolean(errors.timeToTransfer)}
                          helperText={touched.timeToTransfer && errors.timeToTransfer}
                          variant="outlined"
                          className={`${classes.textField} ${
                            touched.timeToTransfer && errors.timeToTransfer ? classes.errorField : classes.background
                          }`}
                          InputProps={{ inputProps: { min: 0 } }}
                          InputLabelProps={{
                            shrink: values.timeToTransfer ? true : false,
                          }}
                          value={values.timeToTransfer < 1 ? "" : values.timeToTransfer}
                          onChange={async (e) => {
                            const nValor = e.target.value === "" ? 0 : e.target.value
                            await setFieldValue("timeToTransfer", nValor, false)
                            setFieldTouched("timeToTransfer", true, false)
                            setFieldTouched("filaTransferencia", true, false)
                            await validateForm()
                          }}
                        />
                      </Grid>
                      <Grid>
                        <QueueSelect
                          modalOpen={open}
                          selectedQueueIds={selectedQueueId || ""}
                          allowedQueueIds={selectedQueueIds}
                          name="filaTransferencia"
                          error={touched.filaTransferencia && Boolean(errors.filaTransferencia)}
                          helperText={
                            touched.filaTransferencia && errors.filaTransferencia
                              ? errors.filaTransferencia
                              : selectedQueueIds.length === 0
                                ? "Inclua ao menos uma fila no campo Filas para escolher a fila de transferência."
                                : undefined
                          }
                          onChange={(selectedId) => {
                            setSelectedQueueId(selectedId)
                            setTimeout(() => {
                              formikRef.current?.validateForm()
                            }, 0)
                            formikRef.current?.setFieldTouched("filaTransferencia", true)
                          }}
                          multiple={false}
                          title={"Fila de transferência"}
                        />
                      </Grid>
                    </Grid>
                  </div>
                </DialogContent>
              </Form>
            </Modal>
          )}
        </Formik>
      )}
    </div>
  )
}

export default React.memo(WhatsAppModal)
